/**
 * Utility function to stop all media tracks
 * This prevents camera/microphone from staying active after leaving a meeting
 */
export const stopAllMediaTracks = (): void => {
  navigator.mediaDevices
    .getUserMedia({ audio: true, video: true })
    .then(stream => {
      if (stream && stream.getTracks) {
        stream.getTracks().forEach(track => {
          if (track && typeof track?.stop === "function") {
            track?.stop();
          }
        });
      }
    })
    .catch(err => {
      console.warn("Error stopping media tracks:", err);
    });

  // Additional cleanup for device enumeration
  if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
    navigator.mediaDevices
      .enumerateDevices()
      .then(() => {
        // Device enumeration successful
      })
      .catch(err => {
        console.warn("Error enumerating devices:", err);
      });
  }
};

/**
 * Utility function to check if remote video is active
 */
export const isRemoteVideoActive = (
  remoteVideoTiles: any,
  remoteAttendeeId: string | null
): boolean => {
  if (!remoteAttendeeId) return false;

  return Array.isArray(remoteVideoTiles)
    ? remoteVideoTiles.some(
        tile =>
          tile.boundAttendeeId === remoteAttendeeId &&
          tile.isContent === false &&
          tile.tileState?.active
      )
    : Object.values(remoteVideoTiles)
        .filter(
          (tile: any) =>
            typeof tile === "object" &&
            tile !== null &&
            "boundAttendeeId" in tile
        )
        .some(
          (tile: any) =>
            tile.boundAttendeeId === remoteAttendeeId &&
            tile.isContent === false &&
            tile.tileState?.active
        );
};

/**
 * Gracefully leave the meeting and release camera/microphone devices.
 * This should be called before navigating away from the meeting screen.
 */
export const cleanUpMeetingResources = async (
  meetingManager: any // Using any to avoid importing full MeetingManager type
): Promise<void> => {
  if (!meetingManager) return;
  try {
    // Stop any ongoing local video tiles and content share
    if (meetingManager.audioVideo) {
      const av = meetingManager.audioVideo;
      av.stopLocalVideoTile();
      av.stopContentShare();

      // Stop the audio/video streams completely
      av.stop();

      // Release the input devices so the browser no longer shows them as "in-use"
      try {
        // These helpers return promises – await to ensure completion
        const avAny = av as any;
        if (typeof avAny.chooseVideoInputDevice === "function") {
          await avAny.chooseVideoInputDevice(null);
        }
        if (typeof avAny.chooseAudioInputDevice === "function") {
          await avAny.chooseAudioInputDevice(null);
        }
      } catch (deviceError) {
        console.warn("Error releasing media devices:", deviceError);
      }
    }

    // Leave the current meeting session
    if (typeof meetingManager.leave === "function") {
      await meetingManager.leave();
    }
  } catch (err) {
    console.error("Error during meeting cleanup:", err);
  } finally {
    // As a final safety, stop any remaining tracks
    stopAllMediaTracks();
  }
};
